import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { NavigationService } from '@treasury/core/navigation';
import { DownloadService } from '@treasury/domain/download';
import { Entitlements, EntitlementsService } from '@treasury/domain/entitlements';
import { LoanService } from '@treasury/domain/loans';
import { Loan } from '@treasury/domain/loans/loan.entity';
import { TmContainer, TmFooterService } from '@treasury/presentation';
import { circlePlusIcon } from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/tm-download';
import { DownloadPageType } from '@treasury/presentation/components/tm-download.types';
import { ButtonConfig } from '@treasury/presentation/components/tm-footer.types';
import '@treasury/presentation/components/tm-sidebar';
import { TmDialogButtonPosture, TmDialogService } from '@treasury/presentation/services/dialog';
import { formatUsdCurrency } from '@treasury/utils';
import { css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import '../../dashboard/components/loan-payment-widget';
import { HeaderBarService } from '../../services/jhd-header-bar.service';
import '../partials/loan-detail-bottom-sheet';
import '../partials/loan-payments-filter-sheet';
import '../partials/loan-payments-table';

export const tagName = 'loans-container';
@customElement(tagName)
export class LoansContainer extends TmContainer {
    @InjectProperty()
    declare private headerService: HeaderBarService;

    @InjectProperty()
    declare private loanService: LoanService;

    @InjectProperty()
    declare private navService: NavigationService;

    @InjectProperty()
    declare private entitlementsService: EntitlementsService;

    @InjectProperty()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    declare private readonly downloadService: DownloadService<any>;

    @InjectProperty()
    declare private dialogService: TmDialogService;

    @InjectProperty()
    declare private readonly footerService: TmFooterService;

    @state()
    private loans: Loan[] = [];

    @state()
    private selectedLoans: Loan[] = [];

    @state()
    private filters: object = {};

    @state()
    private bottomSheetOpen = false;

    @state()
    private loan?: Loan;

    @state()
    private filterSheetOpen = false;

    @state()
    private activeTab = 'One-Time';

    @state()
    private tabs = [{ label: 'One Time', id: 'One-Time' }];

    @state()
    private canApproveOrReject = false;

    @state()
    private comments = '';

    @state()
    private footerButtonConfig: ButtonConfig[] = [
        {
            text: 'Approve loan payments',
            success: true,
            onClick: () => this.showApproveReject('approve'),
        },
        {
            text: 'Reject loan payments',
            error: true,
            onClick: () => this.showApproveReject('reject'),
        },
    ];

    private get loanPaymentTotal() {
        const amount = this.loans?.reduce((a, payment) => a + payment.amount, 0);
        return formatUsdCurrency(amount ?? 0);
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.headerService.configure({
            title: 'Loans',
            menuItems: [
                {
                    title: 'Download',
                    icon: html`<tm-download
                        .page=${DownloadPageType.Loans}
                        .filterModel=${this.filters}
                        .disabled=${true}
                    ></tm-download>`,
                    action: () => undefined,
                },
            ],
        });
        this.configureFooter();
    }

    public disconnectedCallback() {
        super.disconnectedCallback?.();
        this.footerService.hide();
    }

    protected async firstUpdated() {
        this.canApproveOrReject = await this.entitlementsService.isEnabled(
            Entitlements.ApproveTransfer
        );
        this.configureFooter();
    }

    protected render() {
        return html`<tm-sidebar>
                <div
                    class=${classMap({
                        'flex-grow flex flex-col h-full': true,
                        'pb-2': this.isDesktop,
                    })}
                    slot="main-content"
                >
                    <div class="flex-none">
                        ${this.isDesktop ? this.renderTableSummary() : this.renderActionItems()}
                    </div>
                    <div class="flex-grow">${this.renderTable()}</div>
                </div>
                <div slot="sidebar-content">
                    <loan-payment-widget class="mt-4"></loan-payment-widget>
                </div>
            </tm-sidebar>
            ${[this.renderLoanDetailBottomSheet(), this.renderFilterSheet()]}`;
    }

    private async loadLoans(): Promise<void> {
        this.loading = true;
        try {
            this.loans = await this.loanService.getLoanPayments(this.filters);
            this.tabs = [{ label: `One Time (${this.loans.length})`, id: 'One-Time' }];
            this.downloadService.updateConfig({
                filterModel: this.filters,
                isRecurring: false,
                disabled: !this.loans.length,
            });
        } catch {
            this.notificationService.renderError('Failed to load loans');
        } finally {
            this.loading = false;
        }
    }

    private renderFilterSheet() {
        return html`<loan-payments-filter-sheet
            .open=${this.filterSheetOpen}
            @applyFilters=${(e: CustomEvent) => {
                this.filters = e.detail;
                this.loadLoans();
            }}
            @close=${() => {
                this.filterSheetOpen = false;
            }}
        ></loan-payments-filter-sheet>`;
    }

    private renderActionItems() {
        return html`<tm-action-button
            class="mt-[8px] mb-[8px]"
            .buttonTitle=${'Create Loan Payment'}
            @click=${() => {
                this.navService.navigate('/loans/payment/create/new');
            }}
        ></tm-action-button>`;
    }

    private renderTableSummary() {
        return html`<tm-table-summary
            class="mt-2 mx-2 pb-2"
            .summary=${[
                {
                    label: 'Loan payments total',
                    value: this.loanPaymentTotal,
                    state: 'action',
                    action: undefined,
                },
                {
                    label: 'Pending approval',
                    value: this.getStatusCount('PendingApproval'),
                    state: 'attention',
                    action: 'pending-approvals/loanpayment',
                },
                {
                    label: 'Failed loan payments',
                    value: this.getStatusCount('Failed'),
                    state: undefined,
                    action: undefined,
                },
            ]}
        ></tm-table-summary>`;
    }

    private getStatusCount(status: string) {
        return this.loans?.filter(payment => payment.status === status).length ?? 0;
    }

    private renderTable() {
        return html`
            <tm-section
                class=${classMap({
                    'flex flex-col': true,
                    'overflow-hidden h-full bg-page': !this.isDesktop,
                    'h-full mx-2 pb-2 rounded-lg': this.isDesktop,
                })}
            >
                <div
                    class=${classMap({
                        'flex justify-between items-center border-b': true,
                        'bg-primary': !this.isDesktop,
                    })}
                >
                    <tm-tabs
                        .tabs=${this.tabs}
                        .activeTab=${this.activeTab}
                        @switchTab=${(e: CustomEvent) => {
                            this.activeTab = e.detail.activeTab;
                            this.loadLoans();
                        }}
                    ></tm-tabs>
                    ${this.isDesktop
                        ? html`<tm-button
                              importance="primary"
                              .icon=${circlePlusIcon}
                              class="mr-4"
                              @click=${() => {
                                  this.navService.navigate('/loans/payment/create/new');
                              }}
                              >Create loan payment</tm-button
                          >`
                        : nothing}
                </div>
                <div class="flex-grow">
                    <loan-payments-table
                        class="h-full"
                        .loanPayments=${this.loans}
                        .loading=${this.loading}
                        .isDesktop=${this.isDesktop}
                        @loan-payment-canceled=${() => {
                            this.loadLoans();
                        }}
                        @loan-clicked=${(e: CustomEvent) => {
                            this.loan = e.detail;
                            this.bottomSheetOpen = true;
                        }}
                        @selection-changed=${(e: CustomEvent) => {
                            this.selectedLoans = e.detail;
                            this.configureFooter();
                        }}
                        @filter-loan-payments=${() => {
                            this.filterSheetOpen = true;
                        }}
                    ></loan-payments-table>
                </div>
            </tm-section>
        `;
    }

    private configureFooter() {
        if (this.isDesktop && this.canApproveOrReject) {
            this.footerButtonConfig = [
                {
                    text: 'Approve',
                    success: true,
                    disabled: !this.selectedLoans.length,
                    onClick: () => this.showApproveReject('approve'),
                },
                {
                    text: 'Reject',
                    error: true,
                    disabled: !this.selectedLoans.length,
                    onClick: () => this.showApproveReject('reject'),
                },
            ];

            this.footerService.configure({
                direction: 'row',
                buttonConfig: this.footerButtonConfig,
            });
        } else {
            this.footerService.hide();
        }
    }

    private renderLoanDetailBottomSheet() {
        return html`<loan-detail-bottom-sheet
            .bottomSheetOpen=${this.bottomSheetOpen}
            .loan=${this.loan}
            @close=${() => {
                this.bottomSheetOpen = false;
            }}
            @update-loan=${() => {
                this.headerService.configure({ title: 'Loans' });
                this.bottomSheetOpen = false;
                this.loadLoans();
            }}
        ></loan-detail-bottom-sheet>`;
    }

    private async showApproveReject(action: 'approve' | 'reject') {
        const actionText = action === 'approve' ? 'Approve' : 'Reject';
        const { reason } = await this.dialogService.open(
            html`<div>Are you sure you want to ${action} the selected loan payments?</div>
                <br /><tm-text-field
                    label="Comments"
                    @value-changed=${(e: CustomEvent) => {
                        const { value } = e.detail;
                        this.comments = value;
                    }}
                ></tm-text-field>`,
            `${actionText} Loan Payments`,
            {
                buttons: {
                    [TmDialogButtonPosture.Primary]: {
                        label: 'Confirm',
                    },

                    [TmDialogButtonPosture.Secondary]: {
                        label: 'Cancel',
                    },
                },
            }
        );
        if (reason === 'confirm') {
            if (action === 'approve') {
                await this.tryFetch(
                    () =>
                        Promise.all([
                            this.loanService.batchApproveLoanPayments(
                                this.selectedLoans,
                                this.comments
                            ),
                        ]),
                    () => {
                        this.notificationService.renderSuccess(
                            `Loan payments ${action}d successfully`
                        );
                        this.selectedLoans = [];
                        this.comments = '';
                        this.loadLoans();
                    },
                    () => this.notificationService.renderError(`Failed to ${action} loan payments`)
                );
            }
            if (action === 'reject') {
                await this.tryFetch(
                    () =>
                        Promise.all([
                            this.loanService.batchRejectLoanPayments(
                                this.selectedLoans,
                                this.comments
                            ),
                        ]),
                    () => {
                        this.notificationService.renderSuccess(
                            `Loan payments ${action}ed successfully`
                        );
                        this.selectedLoans = [];
                        this.comments = '';
                        this.loadLoans();
                    },
                    () => this.notificationService.renderError(`Failed to ${action} loan payments`)
                );
            }
        }
    }

    public static get styles() {
        return [
            css`
                :host {
                    height: 100%;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: LoansContainer;
    }
}
